import { useEffect, useMemo, useState } from 'react';
import { AppLayoutActions } from '@/components/layout/AppLayoutActions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  AI_SEARCH_QUERY_LENGTH_MESSAGE,
  AI_SEARCH_QUERY_MAX_LENGTH,
  AI_SEARCH_QUERY_MIN_LENGTH,
} from '@/constants/ai-search';
import { parseMultiValueFilter, serializeMultiValueFilter } from '@/routes/-library-route-helpers';
import type { FilterTagGroup } from '@/types';

interface FilterOption {
  slug: string;
  name: string;
}


function normalizeTagSearchValue(value: string): string {
  return value.trim().toLowerCase();
}

function termMatchesTagSearch(term: FilterTagGroup['terms'][number], group: FilterTagGroup, normalizedQuery: string): boolean {
  if (!normalizedQuery) {
    return true;
  }

  return [term.name, term.slug, term.description, group.name, group.slug]
    .some((value) => value.toLowerCase().includes(normalizedQuery));
}

function countSelectedGroupTerms(group: FilterTagGroup, selectedTagSet: Set<string>): number {
  return group.terms.filter((term) => selectedTagSet.has(term.slug)).length;
}

function countGroupItems(group: FilterTagGroup): number {
  return group.terms.reduce((total, term) => total + term.count, 0);
}

interface ItemFilterBarProps {
  queryValue?: string;
  typeValue?: string;
  tagsValue?: string;
  typeOptions: FilterOption[];
  tagGroups: FilterTagGroup[];
  searchLoading?: boolean;
  searchStatusMessage?: string | null;
  searchStatusTone?: 'neutral' | 'error';
  onTypeChange: (value?: string) => void;
  onTagsChange: (value?: string) => void;
  onQuerySubmit: (value?: string) => void;
  statusMessage?: string | null;
}

export function ItemFilterBar({
  queryValue,
  typeValue,
  tagsValue,
  typeOptions,
  tagGroups,
  searchLoading = false,
  searchStatusMessage,
  searchStatusTone = 'neutral',
  onTypeChange,
  onTagsChange,
  onQuerySubmit,
  statusMessage,
}: ItemFilterBarProps) {
  const [queryInput, setQueryInput] = useState(queryValue ?? '');
  const [queryError, setQueryError] = useState<string | null>(null);
  const [tagSearchInput, setTagSearchInput] = useState('');
  const [collapsedTagGroups, setCollapsedTagGroups] = useState<Record<string, boolean>>({});
  const selectedTagValues = parseMultiValueFilter(tagsValue);
  const selectedTagSet = useMemo(() => new Set(selectedTagValues), [selectedTagValues]);
  const hasSubmittedQuery = Boolean(queryValue?.trim());
  const normalizedTagSearch = normalizeTagSearchValue(tagSearchInput);
  const hasTagSearch = normalizedTagSearch.length > 0;
  const visibleTagGroups = useMemo(
    () => tagGroups
      .map((group) => ({
        ...group,
        terms: group.terms.filter((term) => termMatchesTagSearch(term, group, normalizedTagSearch)),
      }))
      .filter((group) => group.terms.length > 0),
    [normalizedTagSearch, tagGroups],
  );

  useEffect(() => {
    setQueryInput(queryValue ?? '');
    setQueryError(null);
  }, [queryValue]);

  const handleTagToggle = (slug: string, checked: boolean) => {
    const nextValues = checked
      ? [...selectedTagValues, slug]
      : selectedTagValues.filter((selectedValue) => selectedValue !== slug);

    onTagsChange(serializeMultiValueFilter(nextValues));
  };

  const setAllVisibleTagGroupsCollapsed = (collapsed: boolean) => {
    setCollapsedTagGroups((current) => ({
      ...current,
      ...Object.fromEntries(visibleTagGroups.map((group) => [group.slug, collapsed])),
    }));
  };

  return (
    <section className="w-full border border-[#ddd] bg-[#fdfdfd] p-4" data-ai-id="library-item-filter-bar">
      <div className="grid gap-3" data-ai-id="library-item-filter-bar-row">
        <form
          className="space-y-2 border border-[#e5e7eb] bg-white p-3"
          onSubmit={(event) => {
            event.preventDefault();
            const normalizedQuery = queryInput.trim();

            if (
              normalizedQuery.length > 0
              && (normalizedQuery.length < AI_SEARCH_QUERY_MIN_LENGTH || normalizedQuery.length > AI_SEARCH_QUERY_MAX_LENGTH)
            ) {
              setQueryError(AI_SEARCH_QUERY_LENGTH_MESSAGE);
              return;
            }

            setQueryError(null);
            onQuerySubmit(normalizedQuery || undefined);
          }}
          data-ai-id="library-item-filter-ai-search-form"
        >
          <Input
            label="AI Search"
            value={queryInput}
            onChange={(event) => {
              setQueryInput(event.target.value);
              if (queryError) {
                setQueryError(null);
              }
            }}
            placeholder="e.g. trustworthy hero for a law firm"
            autoComplete="off"
            maxLength={AI_SEARCH_QUERY_MAX_LENGTH}
            error={queryError ?? undefined}
            aiIdPrefix="library-item-filter-ai-search-input"
          />
          <div
            className="flex flex-wrap items-center gap-2"
            data-ai-id="library-item-filter-ai-search-actions"
          >
            <Button
              type="submit"
              size="sm"
              loading={searchLoading}
              aiIdPrefix="library-item-filter-ai-search-submit-button"
              data-ai-id="library-item-filter-ai-search-submit-button"
            >
              Search
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!hasSubmittedQuery && queryInput.trim().length === 0}
              onClick={() => {
                setQueryInput('');
                onQuerySubmit(undefined);
              }}
              aiIdPrefix="library-item-filter-ai-search-clear-button"
              data-ai-id="library-item-filter-ai-search-clear-button"
            >
              Clear AI search
            </Button>
          </div>
          <p className="text-xs text-slate-500" data-ai-id="library-item-filter-ai-search-helper">
            Ask in natural language, then narrow the recommendations with the manual type and tag filters below.
          </p>
          {searchStatusMessage && !queryError ? (
            <p
              className={['text-xs', searchStatusTone === 'error' ? 'text-rose-600' : 'text-slate-500'].join(' ')}
              role={searchStatusTone === 'error' ? 'alert' : undefined}
              data-ai-id="library-item-filter-ai-search-status-message"
            >
              {searchStatusMessage}
            </p>
          ) : null}
        </form>

        <fieldset className="space-y-1.5 text-sm" data-ai-id="library-item-filter-type-field">
          <span className="font-medium text-slate-700" data-ai-id="library-item-filter-type-label">
            Type
          </span>
          <div
            className="flex min-h-[34px] flex-wrap gap-2"
            data-ai-id="library-item-filter-type-select"
          >
            <label
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap border border-[#ccc] bg-[#f9f9f9] px-2 py-1 text-[13px] text-slate-700"
              data-ai-id="library-item-filter-type-option-all"
            >
              <input
                type="checkbox"
                checked={!typeValue}
                onChange={(event) => {
                  if (event.target.checked) {
                    onTypeChange(undefined);
                  }
                }}
                data-ai-id="library-item-filter-type-checkbox-all"
              />
              <span>All</span>
            </label>
            {typeOptions.map((option) => (
              <label
                key={option.slug}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap border border-[#ccc] bg-[#f9f9f9] px-2 py-1 text-[13px] text-slate-700"
                data-ai-id="library-item-filter-type-option"
              >
                <input
                  type="checkbox"
                  checked={typeValue === option.slug}
                  onChange={(event) => onTypeChange(event.target.checked ? option.slug : undefined)}
                  data-ai-id="library-item-filter-type-checkbox"
                />
                <span>{option.name}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-3 text-sm" data-ai-id="library-item-filter-tags-field">
          <div className="flex flex-wrap items-center justify-between gap-2" data-ai-id="library-item-filter-tags-header">
            <div className="space-y-1">
              <span className="font-medium text-slate-700" data-ai-id="library-item-filter-tags-label">
                Tags
              </span>
              <p className="text-xs text-slate-500" data-ai-id="library-item-filter-tags-helper">
                Filter by the full controlled vocabulary. Search, expand, or collapse groups to keep the larger tag set manageable.
              </p>
            </div>
            <button
              type="button"
              className="border border-[#ccc] bg-[#f9f9f9] px-2 py-1 text-[12px] font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => onTagsChange(undefined)}
              disabled={selectedTagValues.length === 0}
              data-ai-id="library-item-filter-tags-clear-button"
            >
              Clear tags
            </button>
          </div>

          {tagGroups.length > 0 ? (
            <div className="space-y-3" data-ai-id="library-item-filter-tag-browser">
              <div className="grid gap-2 border border-[#e5e7eb] bg-white p-3" data-ai-id="library-item-filter-tag-tools">
                <Input
                  label="Find tags"
                  value={tagSearchInput}
                  onChange={(event) => setTagSearchInput(event.target.value)}
                  placeholder="Search role, layout, visual style, industry, or slug"
                  autoComplete="off"
                  aiIdPrefix="library-item-filter-tag-search-input"
                />
                <div className="flex flex-wrap items-center justify-between gap-2" data-ai-id="library-item-filter-tag-tools-actions">
                  <p className="text-xs text-slate-500" data-ai-id="library-item-filter-tag-tools-summary">
                    Showing {visibleTagGroups.length} of {tagGroups.length} groups
                    {selectedTagValues.length > 0 ? ` · ${selectedTagValues.length} selected` : ''}
                    {hasTagSearch ? ` · matching “${tagSearchInput.trim()}”` : ''}
                  </p>
                  <div className="flex flex-wrap gap-2" data-ai-id="library-item-filter-tag-tools-buttons">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setAllVisibleTagGroupsCollapsed(false)}
                      disabled={visibleTagGroups.length === 0}
                      aiIdPrefix="library-item-filter-tags-expand-all-button"
                      data-ai-id="library-item-filter-tags-expand-all-button"
                    >
                      Expand all
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setAllVisibleTagGroupsCollapsed(true)}
                      disabled={visibleTagGroups.length === 0}
                      aiIdPrefix="library-item-filter-tags-collapse-all-button"
                      data-ai-id="library-item-filter-tags-collapse-all-button"
                    >
                      Collapse all
                    </Button>
                  </div>
                </div>
              </div>

              {visibleTagGroups.length > 0 ? (
                <div className="space-y-3" data-ai-id="library-item-filter-tag-groups">
                  {visibleTagGroups.map((group, groupIndex) => {
                    const selectedCount = countSelectedGroupTerms(group, selectedTagSet);
                    const itemCount = countGroupItems(group);
                    const isCollapsed = collapsedTagGroups[group.slug] ?? (!hasTagSearch && selectedCount === 0 && groupIndex > 1);

                    return (
                      <section
                        key={group.slug}
                        className="border border-[#e5e7eb] bg-white p-3"
                        data-ai-id="library-item-filter-tag-group"
                      >
                        <button
                          type="button"
                          className="flex w-full items-start justify-between gap-3 text-left"
                          onClick={() => {
                            setCollapsedTagGroups((current) => ({
                              ...current,
                              [group.slug]: !isCollapsed,
                            }));
                          }}
                          aria-expanded={!isCollapsed}
                          data-ai-id="library-item-filter-tag-group-toggle"
                        >
                          <span className="min-w-0" data-ai-id="library-item-filter-tag-group-header">
                            <span className="block font-medium text-slate-800" data-ai-id="library-item-filter-tag-group-name">
                              {group.name}
                            </span>
                            {group.description ? (
                              <span className="mt-0.5 block text-xs text-slate-500" data-ai-id="library-item-filter-tag-group-description">
                                {group.description}
                              </span>
                            ) : null}
                            <span className="mt-1 block text-[11px] text-slate-500" data-ai-id="library-item-filter-tag-group-summary">
                              {group.terms.length} tags · {itemCount} items{selectedCount > 0 ? ` · ${selectedCount} selected` : ''}
                            </span>
                          </span>
                          <span className="shrink-0 border border-[#ccc] bg-[#f9f9f9] px-2 py-1 text-[11px] font-medium text-slate-700" data-ai-id="library-item-filter-tag-group-toggle-label">
                            {isCollapsed ? 'Show' : 'Hide'}
                          </span>
                        </button>

                        {isCollapsed ? null : (
                          <div className="mt-3 flex flex-wrap gap-2" data-ai-id="library-item-filter-tag-group-terms">
                            {group.terms.map((term) => {
                              const checked = selectedTagSet.has(term.slug);

                              return (
                                <label
                                  key={term.slug}
                                  className={[
                                    'inline-flex cursor-pointer items-center gap-2 border px-2 py-1 text-[13px]',
                                    checked ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-[#ccc] bg-[#f9f9f9] text-slate-700',
                                  ].join(' ')}
                                  data-ai-id="library-item-filter-tag-option"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(event) => handleTagToggle(term.slug, event.target.checked)}
                                    data-ai-id="library-item-filter-tag-checkbox"
                                  />
                                  <span>{term.name}</span>
                                  <span className="text-[11px] text-slate-500" data-ai-id="library-item-filter-tag-count">
                                    ({term.count})
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : (
                <p className="border border-[#e5e7eb] bg-white p-3 text-xs text-slate-500" data-ai-id="library-item-filter-tags-no-search-results">
                  No tag filters match your search.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500" data-ai-id="library-item-filter-tags-empty">
              No tag filters available right now.
            </p>
          )}
        </fieldset>
      </div>

      <div className="mt-4 border-t border-[#ddd] pt-4" data-ai-id="library-item-filter-bar-actions">
        <AppLayoutActions />
      </div>

      {statusMessage ? (
        <p className="mt-3 text-xs text-slate-500" data-ai-id="library-item-filter-status-message">
          {statusMessage}
        </p>
      ) : null}
    </section>
  );
}
